/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * fetchAllRows :: read an entire result set past PostgREST's row cap.
 *
 * PostgREST caps every response at `db-max-rows` (1000 on this project) and
 * says so ONLY in the Content-Range header — the JSON body just arrives short.
 * Every read path used to take that truncated page at face value, so any
 * workspace with more than 1000 depletions (13 states x 18 SKUs x 19 months is
 * already 4,400) silently rendered a partial grid, and the operations-hub KPIs
 * computed off it were simply wrong.
 *
 * This walks the result set with .range() until a short page comes back.
 *
 * `buildQuery` MUST return a NEW query builder each call — a Supabase builder
 * is a one-shot thenable and cannot be re-ranged after it has been awaited.
 *
 *   const rows = await fetchAllRows(
 *     () => supabase.from("depletions").select("*").eq("account_id", acct),
 *     { label: "depletions" }
 *   );
 */

const DEFAULT_PAGE_SIZE = 1000;
// A stop so a runaway loop can't hang the tab. Well above any real workspace;
// if it ever trips that's a signal, and we say so rather than truncating quietly.
const DEFAULT_MAX_ROWS = 200000;

export async function fetchAllRows(buildQuery, options = {}) {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    maxRows = DEFAULT_MAX_ROWS,
    label = "rows",
    // `strict: true` turns hitting the cap into a THROW instead of a short list.
    // Use it whenever the caller is going to act on the result set as if it were
    // complete — the import swap (APP-334) deletes exactly the ids this returns,
    // so a silently truncated list there leaves superseded rows behind forever
    // while reporting success. A subset is fine for rendering; it is never fine
    // for deciding what to delete.
    strict = false,
  } = options;

  const all = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) throw error;

    const page = data ?? [];
    all.push(...page);

    if (page.length < pageSize) break; // short page → that was the last one
    if (all.length >= maxRows) {
      const msg =
        `[fetchAllRows] ${label}: stopped at ${all.length} rows (cap ${maxRows}). ` +
        "The result set is larger than expected — this view is showing a subset.";
      if (strict) throw new Error(msg);
      console.error(msg);
      break;
    }
  }
  return all;
}
